import React, { useState, useEffect, useRef, useCallback } from "react";
import { useSearchParams, useLocation } from "react-router-dom";
import { adminApi } from "../services/api";
import MultiSelectDropdown from "../components/common/MultiSelectDropdown";
import Table from "../components/common/Table";
import TableLoader from "../components/common/TableLoader";
import Tooltip from "../components/common/Tooltip";
import { formatPercentage } from "../utils";

const AuditLogsPage = () => {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [logs, setLogs] = useState([]);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [isRefetching, setIsRefetching] = useState(false);
  const [error, setError] = useState("");
  
  // Initialize state from navigation state or URL params directly during component mount
  const [query, setQuery] = useState(() => {
    if (location.state?.filters?.query) return location.state.filters.query;
    return searchParams.get("query") || "";
  });
  const [statusFilter, setStatusFilter] = useState(() => {
    if (location.state?.filters?.statusFilter) return location.state.filters.statusFilter;
    const param = searchParams.get("statusFilter");
    return param ? param.split(",") : [];
  });
  const [methodFilter, setMethodFilter] = useState(() => {
    if (location.state?.filters?.methodFilter) return location.state.filters.methodFilter;
    const param = searchParams.get("methodFilter");
    return param ? param.split(",") : [];
  });
  const [endpointFilter, setEndpointFilter] = useState(() => {
    if (location.state?.filters?.endpointFilter) return location.state.filters.endpointFilter;
    const param = searchParams.get("endpointFilter");
    return param ? param.split(",") : [];
  });
  const [groupBy, setGroupBy] = useState(() => {
    if (location.state?.filters?.groupBy) return location.state.filters.groupBy;
    return searchParams.get("groupBy") || "none";
  });
  const [pagination, setPagination] = useState(() => {
    if (location.state?.pagination) {
      return {
        page: location.state.pagination.page || 1,
        limit: location.state.pagination.limit || 10,
        total: 0,
        totalPages: 0,
      };
    }
    const pageParam = searchParams.get("page");
    const limitParam = searchParams.get("limit");
    return {
      page: pageParam ? parseInt(pageParam) : 1,
      limit: limitParam ? parseInt(limitParam) : 10,
      total: 0,
      totalPages: 0,
    };
  });
  const [filterOptions, setFilterOptions] = useState({
    methods: [],
    endpoints: [],
    responseStatuses: [],
  });
  const isPaginationChange = useRef(false);
  const isInitializing = useRef(true);
  const isRestoringFromNavigation = useRef(false);
  const [stats, setStats] = useState({
    totalLogs: 0,
    errorLogs: 0,
    errorRate: 0,
  });
  const [entityOptions, setEntityOptions] = useState([]);

  useEffect(() => {
    fetchFilterOptions();
    fetchStats();

    // Update URL params when returning from navigation state
    if (location.state?.filters) {
      isRestoringFromNavigation.current = true;
      const { filters, pagination: savedPagination } = location.state;

      // Update URL params to match restored state
      const params = new URLSearchParams();
      if (filters.query) params.set("query", filters.query);
      if (filters.statusFilter?.length > 0) params.set("statusFilter", filters.statusFilter.join(","));
      if (filters.methodFilter?.length > 0) params.set("methodFilter", filters.methodFilter.join(","));
      if (filters.endpointFilter?.length > 0) params.set("endpointFilter", filters.endpointFilter.join(","));
      if (filters.groupBy && filters.groupBy !== "none") params.set("groupBy", filters.groupBy);
      // Always set pagination params when restoring from navigation, regardless of default values
      if (savedPagination?.page) params.set("page", savedPagination.page);
      if (savedPagination?.limit) params.set("limit", savedPagination.limit);
      setSearchParams(params);
      
      // Reset flag after a short delay
      setTimeout(() => {
        isRestoringFromNavigation.current = false;
      }, 100);
    }
    
    // Mark initialization as complete
    isInitializing.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Effect for pagination changes (non-debounced)
  useEffect(() => {
    if (isPaginationChange.current) {
      isPaginationChange.current = false;
      return;
    }
  }, [pagination.page, pagination.limit]);

  // Main data fetching effect
  useEffect(() => {
    // Skip data fetching during initialization to avoid race conditions with URL params
    if (isInitializing.current) {
      return;
    }

    fetchLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.page, pagination.limit, query, statusFilter, methodFilter, endpointFilter, groupBy]);

  const fetchFilterOptions = async () => {
    try {
      const filtersResponse = await adminApi.getAuditLogFilters();

      setFilterOptions((prev) => ({
        ...prev,
        ...filtersResponse.data,
      }));
    } catch (err) {
      console.error("Failed to load filter options", err);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await adminApi.getAuditLogStats();
      setStats(response.data.stats);

      const entityStats = response.data.stats?.entityStats || [];
      const options = entityStats
        .filter((item) => item.entity)
        .map((item) => ({ key: item.entity, label: item.name || item.entity }))
        .sort((a, b) => a.label.localeCompare(b.label));
      setEntityOptions(options);
    } catch (err) {
      console.error("Failed to load stats", err);
    }
  };

  const fetchLogs = async () => {
    if (isInitialLoading) {
      setIsInitialLoading(true);
    } else {
      setIsRefetching(true);
    }
    try {
      const filters = {};
      if (query) filters.endpoint = query;
      if (statusFilter.length > 0) filters.responseStatus = statusFilter.join(",");
      if (methodFilter.length > 0) filters.method = methodFilter.join(",");
      if (endpointFilter.length > 0) filters.endpoint = endpointFilter.join(",");
      if (groupBy !== "none") filters.entity = groupBy;

      const response = await adminApi.getAuditLogs({
        ...filters,
        page: pagination.page,
        limit: pagination.limit,
      });
      setLogs(response.data.logs);
      setPagination((prev) => ({
        ...prev,
        ...response.data.pagination,
      }));
    } catch (err) {
      setError("Failed to load audit logs");
      console.error(err);
    } finally {
      setIsInitialLoading(false);
      setIsRefetching(false);
    }
  };

  const handleApplyFilters = () => {
    setPagination((prev) => ({ ...prev, page: 1 }));
    fetchLogs();
  };

  const clearFilters = () => {
    setQuery("");
    setStatusFilter([]);
    setMethodFilter([]);
    setEndpointFilter([]);
    setGroupBy("none");
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  // Wrapper functions to reset page when filters change
  const handleQueryChange = (value) => {
    setQuery(value);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleStatusFilterChange = (value) => {
    setStatusFilter(value);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleMethodFilterChange = (value) => {
    setMethodFilter(value);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleEndpointFilterChange = (value) => {
    setEndpointFilter(value);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleGroupByChange = (value) => {
    setGroupBy(value);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const updateURLParams = () => {
    // Skip URL update when restoring from navigation state to avoid overwriting
    if (isRestoringFromNavigation.current) {
      return;
    }
    
    const params = new URLSearchParams();
    if (query) params.set("query", query);
    if (statusFilter.length > 0) params.set("statusFilter", statusFilter.join(","));
    if (methodFilter.length > 0) params.set("methodFilter", methodFilter.join(","));
    if (endpointFilter.length > 0) params.set("endpointFilter", endpointFilter.join(","));
    if (groupBy !== "none") params.set("groupBy", groupBy);
    if (pagination.page !== 1) params.set("page", pagination.page);
    if (pagination.limit !== 10) params.set("limit", pagination.limit);

    setSearchParams(params);
  };

  useEffect(() => {
    updateURLParams();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, statusFilter, methodFilter, endpointFilter, groupBy, pagination.page, pagination.limit]);

  const getMethodColor = (method) => {
    const colors = {
      GET: "bg-blue-100 text-blue-700",
      POST: "bg-green-100 text-green-800",
      PUT: "bg-yellow-100 text-yellow-800",
      PATCH: "bg-purple-100 text-purple-800",
      DELETE: "bg-red-100 text-red-800",
    };
    return colors[method] || "bg-gray-100 text-gray-800";
  };

  const getStatusColor = (status) => {
    if (status === "success") return "bg-green-100 text-green-800";
    if (status === "error") return "bg-red-100 text-red-800";
    return "bg-yellow-100 text-yellow-800";
  };

  const handlePageChange = useCallback((newPage) => {
    isPaginationChange.current = true;
    setPagination((prev) => ({ ...prev, page: newPage }));
  }, []);

  const handlePageSizeChange = useCallback((newPageSize) => {
    isPaginationChange.current = true;
    setPagination((prev) => ({ ...prev, limit: newPageSize, page: 1 }));
  }, []);

  const columns = [
    {
      key: "endpoint",
      label: "API Endpoint",
      render: (l) => (
        <div className="flex items-center gap-2" title={l.endpoint + (l.queryParams ? `?${new URLSearchParams(l.queryParams).toString()}` : "")}>
          <span className={`text-[10px] font-bold py-0.5 px-1.5 rounded tracking-wide ${getMethodColor(l.method)}`}>{l.method}</span>
          <span className="font-mono text-[11px] text-ink truncate">{l.endpoint}</span>
        </div>
      ),
    },
    {
      key: "response",
      label: "Retrieval Counts",
      className: "text-center",
      cellClassName: "text-center",
      render: (l) => (
        <span className="font-mono text-[11px] text-muted whitespace-nowrap">{l.responseBody?.data?.counts ?? 0}</span>
      ),
    },
    {
      key: "status",
      label: "Status",
      className: "text-center",
      cellClassName: "text-center",
      render: (l) => (
        <div className="flex items-center justify-center gap-2">
          <span className="text-[11px] font-medium">{l.status}</span>
          <Tooltip
            content={l.responseStatus === "error" ? l.errorMessage || "Error occurred" : l.successMessage || "Request successful"}
            position="top"
          >
            <span className={`inline-flex items-center gap-1 py-0.5 px-2 rounded-full text-[11px] font-semibold ${getStatusColor(l.responseStatus)}`}>
              <span
                className={`w-1.5 h-1.5 rounded-full ${
                  l.responseStatus === "success" ? "bg-green-600" : l.responseStatus === "error" ? "bg-red-600" : "bg-yellow-600"
                }`}
              ></span>
              {l.responseStatus}
            </span>
          </Tooltip>
        </div>
      ),
    },
    // {
    //   key: "endpoint",
    //   label: "Endpoint",
    //   render: (l) => <span className="inline-block py-0.5 px-1.5 border border-line rounded text-[11px] text-ink bg-soft">{l.endpoint || "—"}</span>,
    // },
    {
      key: "timestamp",
      label: "Timestamp",
      className: "text-center",
      cellClassName: "text-center",
      render: (l) => <span className="font-mono text-[11px] text-muted whitespace-nowrap">{new Date(l.timestamp).toLocaleString()}</span>,
    },
    {
      key: "source",
      label: "Source",
      className: "text-center",
      cellClassName: "text-center",
      render: (l) => (
        <span className="inline-block py-0.5 px-2 border border-line rounded text-[11px] text-ink bg-soft capitalize">
          {l.source || "—"}
        </span>
      ),
    },
    {
      key: "duration",
      label: "Duration",
      className: "text-center",
      cellClassName: "text-center",
      render: (l) => <span className="font-mono text-[11px] text-muted">{l.duration ? `${(l.duration / 1000).toFixed(2)}s` : "—"}</span>,
    },
  ];

  return (
    <div className="px-6 py-8 pb-24 font-sans text-ink bg-white h-full">
      <div className="mb-8">
        <h1 className="text-[28px] font-bold tracking-tight m-0">Audit Logs</h1>
        <p className="text-muted text-[15px] mt-2 mb-0">Track user actions, API calls, and response statuses across your QuickBooks integrations.</p>
      </div>

      <div className="grid grid-cols-5 gap-4 mb-6">
        <div className="border border-line rounded-xl p-5 bg-white">
          <div className="text-xs text-muted uppercase tracking-wider font-semibold">Total events</div>
          <div className="text-[26px] font-bold mt-1.5 tracking-tight">{stats.totalLogs}</div>
        </div>
        <div className="border border-line rounded-xl p-5 bg-white">
          <div className="text-xs text-muted uppercase tracking-wider font-semibold">Success</div>
          <div className="text-[26px] font-bold mt-1.5 tracking-tight text-green-700">{stats.totalLogs - stats.errorLogs}</div>
        </div>
        <div className="border border-line rounded-xl p-5 bg-white">
          <div className="text-xs text-muted uppercase tracking-wider font-semibold">Failed</div>
          <div className="text-[26px] font-bold mt-1.5 tracking-tight text-red-700">{stats.errorLogs}</div>
        </div>
        <div className="border border-line rounded-xl p-5 bg-white">
          <div className="text-xs text-muted uppercase tracking-wider font-semibold">Error Rate</div>
          <div className="text-[26px] font-bold mt-1.5 tracking-tight text-yellow-700">{formatPercentage(stats?.errorRate)}%</div>
        </div>
        <div className="border border-line rounded-xl p-5 bg-white">
          <div className="text-xs text-muted uppercase tracking-wider font-semibold">Success Rate</div>
          <div className="text-[26px] font-bold mt-1.5 tracking-tight text-green-700">
            {stats?.errorRate && formatPercentage(100 - stats?.errorRate)}%
          </div>
        </div>
      </div>

      <div className="mb-5">
        <div className="flex gap-3 mb-3 items-center flex-wrap">
          <div className="relative flex-1 min-w-[240px]">
            <svg
              className="absolute left-3 top-1/2 -translate-y-1/2 text-muted"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            >
              <circle cx="11" cy="11" r="7" />
              <path d="m20 20-3.5-3.5" />
            </svg>
            <input
              placeholder="Search endpoint…"
              value={query}
              onChange={(e) => handleQueryChange(e.target.value)}
              className="w-full py-2.5 px-3 pl-9 border border-line rounded-lg text-[13.5px] bg-white transition-all focus:outline-none focus:border-indigo focus:ring-3 focus:ring-indigo/10"
            />
          </div>

          <MultiSelectDropdown
            options={[
              { label: "Success", value: "success" },
              { label: "Error", value: "error" },
              { label: "Redirect", value: "redirect" },
            ]}
            value={statusFilter}
            onSelect={handleStatusFilterChange}
            buttonLabel="Status"
            searchable={false}
          />

          <MultiSelectDropdown
            options={[
              { label: "GET", value: "GET" },
              { label: "POST", value: "POST" },
              { label: "PUT", value: "PUT" },
              { label: "PATCH", value: "PATCH" },
              { label: "DELETE", value: "DELETE" },
            ]}
            value={methodFilter}
            onSelect={handleMethodFilterChange}
            buttonLabel="Method"
            searchable={false}
            renderBadge={(option) => (
              <span
                className={`font-bold ${
                  option.value === "GET"
                    ? "text-blue-700"
                    : option.value === "POST"
                      ? "text-green-800"
                      : option.value === "PUT"
                        ? "text-yellow-800"
                        : option.value === "PATCH"
                          ? "text-purple-800"
                          : option.value === "DELETE"
                            ? "text-red-800"
                            : "text-ink"
                }`}
              >
                {option.label}
              </span>
            )}
          />

          {/* <MultiSelectDropdown
            options={filterOptions.endpoints.map((e) => ({ label: e, value: e }))}
            value={endpointFilter}
            onSelect={handleEndpointFilterChange}
            buttonLabel="Endpoint"
            searchable={true}
          /> */}

          <div className="relative min-w-[160px]">
            <select
              value={groupBy}
              onChange={(e) => handleGroupByChange(e.target.value)}
              className="w-full py-2.5 px-3.5 pr-10 border border-line rounded-lg bg-white cursor-pointer text-[13px] font-medium text-ink hover:bg-soft transition-all appearance-none focus:outline-none focus:border-indigo focus:ring-3 focus:ring-indigo/10"
            >
              <option value="none">Group By: None</option>
              {entityOptions.map((opt) => (
                <option key={opt.key} value={opt.key}>
                  Group By: {opt.label}
                </option>
              ))}
            </select>
            <svg
              className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-muted"
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="6 9 12 15 18 9"></polyline>
            </svg>
          </div>

          <button
            onClick={clearFilters}
            disabled={methodFilter.length === 0 && endpointFilter.length === 0 && statusFilter.length === 0 && query === "" && groupBy === "none"}
            className="py-2.5 px-3.5 border border-line rounded-lg bg-[#1e293b] cursor-pointer text-[13px] font-medium text-white hover:text-white hover:bg-[#334155] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Reset
          </button>
        </div>
      </div>

      <div className="relative">
        <Table
          columns={columns}
          data={logs}
          loading={isInitialLoading}
          error={error}
          pagination={pagination}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          maxHeight="calc(100vh - 300px)"
          emptyState={
            <tr>
              <td colSpan={6} className="py-20 px-4">
                <div className="flex flex-col items-center justify-center text-center">
                  <svg
                    width="64"
                    height="64"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#9ca3af"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="mb-4"
                  >
                    <path d="14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                    <polyline points="14 2 14 8 20 8"></polyline>
                    <line x1="12" y1="18" x2="12" y2="12"></line>
                    <line x1="9" y1="15" x2="15" y2="15"></line>
                  </svg>
                  <h3 className="text-lg font-semibold text-ink mb-2">No Audit Logs Found</h3>
                  <p className="text-sm text-muted max-w-md">
                    {statusFilter.length > 0 || methodFilter.length > 0 || endpointFilter.length > 0 || query
                      ? "No logs match your current filters. Try adjusting your search criteria."
                      : "No audit logs have been recorded yet. Logs will appear here once API activity begins."}
                  </p>
                </div>
              </td>
            </tr>
          }
        />
        {isRefetching && <TableLoader overlay={true} message="Loading..." />}
      </div>
    </div>
  );
};

export default AuditLogsPage;
